import { Constants } from '@praktio/common';
import { Dispatch } from 'redux';
import { v4 as uuidv4 } from 'uuid';

import {
  getNextSlideForLearner,
  getPreviousSlideForLearner,
  getCourseForLearner,
  updateHideModuleExercisesForLearner,
  getModuleForLearner,
  getResourceForLearner,
  markViewedForLearner,
  getSlideForLearner,
} from '../utils/api';
import { stopPlayer } from './explainerReducer';
import { update as updateCourse } from './coursesLitmosReducer';
import { update as updateQuiz } from './quizLitmosReducer';
import { update as updateLearner } from './learnerReducer';
import { getCurrentSlide, isExerciseSlide, processAssets } from '../utils/slide';
import {
  determineQuizData,
  notifyError,
  notifySlideLoaded,
  notifySlideLoading,
  postSlideData,
  resetStatus,
  setCompleted,
  showNextModule,
} from '../utils/learnerUtils';
import { load, success } from './loadLitmosReducer';
import { cloneDeep } from 'lodash';

const { ResourceTypes, CustomNavType, TemplateTypes, ResourceItemTypes } = Constants;

const searchParams = new URLSearchParams(window.location.search);
const SlideIncluded =
  searchParams && searchParams.has('slideIncluded') ? !!searchParams.get('slideIncluded') : false;
const UseSession =
  searchParams && searchParams.has('useSession') ? !!searchParams.get('useSession') : false;

export const actions = {
  UPDATE: 'slidesLitmos/update',
  CLEAR: 'slidesLitmos/clear',
};

interface TimelineItem {
  type: string;
}

interface SlidePayload {
  currentSlide?: Slide;
  data?: Module;
  navItemId?: string;
  noAccess?: boolean;
  settings?: { color?: string; font?: string };
  timeline?: TimelineItem[];
  loading?: boolean;
  slideError?: boolean;
  index?: number;
}

interface SlideAction {
  type: typeof actions.UPDATE | typeof actions.CLEAR;
  payload: SlidePayload;
}

type SlideActionTypes = SlideAction;

export const loadSlideResources = async ({
  slide,
  learnerId,
}: {
  slide: Slide;
  learnerId: string;
}) => {
  console.log('Getting slide resources...');
  const processedIds: { [index: string]: boolean } = {};
  const resources = [];
  for (let asset of slide.timeline) {
    const resourceId = asset.resourceId;
    if (resourceId && !processedIds[resourceId]) {
      const response = await getResourceForLearner({
        learnerId,
        slideId: slide._id,
        resourceId,
      });
      resources.push(response.data);
      processedIds[resourceId] = true;
    }

    // Fetch imageId resources for Answer items (CATA image swap)
    const imageId = asset.imageId;
    if (asset.type === ResourceItemTypes.Answer && imageId && !processedIds[imageId]) {
      const response = await getResourceForLearner({
        learnerId,
        slideId: slide._id,
        resourceId: imageId,
      });

      resources.push(response.data);
      processedIds[imageId] = true;
    }
  }

  if (slide.audioId) {
    const response = await getResourceForLearner({
      learnerId,
      slideId: slide._id,
      resourceId: slide.audioId,
    });
    slide.audio = response.data;
  }

  if (slide.referenceContentId) {
    const response = await getResourceForLearner({
      learnerId,
      slideId: slide._id,
      resourceId: slide.referenceContentId,
    });
    slide.referenceContent = response.data;
  }

  const allResourceIds = [];
  // Get all resources defined in the resource list
  const assets = slide.timeline;
  if (assets) {
    assets
      .filter((a) => a.resourceId)
      .forEach((a) => {
        allResourceIds.push(a.resourceId);
      });
  }

  processAssets(slide, resources, true);
  if (slide.panels.length === 3) {
    const id = slide.panels[1]._id;
    const panelContainsDocument = slide.timeline.find(
      (r) => r.panelId === id && r.type === ResourceTypes.Document,
    );

    slide.panels[0].width = 0.32;
    slide.panels[1].width = panelContainsDocument ? 0.4 : 0.28;
    slide.panels[2].width = panelContainsDocument ? 0.28 : 0.4;
  }

  return slide;
};

export const update = (payload: SlidePayload) => {
  return {
    type: actions.UPDATE,
    payload,
  };
};

export const clear = () => {
  return {
    type: actions.CLEAR,
  };
};

export const updateResource = (resource: Annotation, data?: Annotation) => {
  return (dispatch: Dispatch, getState: Function) => {
    const r = { ...resource };
    if (data) {
      if (r.type && [ResourceTypes.Document].includes(r.type)) {
        r.content = data.content;
        r.indexedContent = data.indexedContent;
      } else if (r.type === ResourceTypes.Stateful) {
        r.files = data.files;
      }
    }

    const state = getState();
    const currentSlide = { ...getCurrentSlide(state) } as Slide;
    const index = currentSlide.timeline
      ? currentSlide.timeline.findIndex((tr) => tr._id === r._id)
      : -1;
    if (index > -1) {
      currentSlide.timeline.splice(index, 1, r);
    }
    dispatch(
      update({
        currentSlide,
      }),
    );
  };
};

export const getUrlsForSlide = (slideId: string, assets?: ModuleAsset[]): string[] =>
  (assets ?? []).filter((a) => a.slideIds.includes(slideId)).map((a) => a.url);

// How many background images load at once. Capped so the background warming
// never saturates the connection — fetchpriority alone can't preempt requests
// that are already in flight, so we keep only a few in flight at a time, leaving
// headroom for the current slide's high-priority gate loads on navigation.
const BACKGROUND_CONCURRENCY = 4;

const preloadAssets = (urls: string[]) => {
  const win = window as Win;
  const preloaded: HtmlImage[] = win.preloaded ?? [];
  win.preloaded = preloaded;
  let next = 0;
  const loadNext = () => {
    if (next >= urls.length) {
      return;
    }
    const url = urls[next];
    next += 1;
    const img = new Image() as HtmlImage;
    // Background warming: low priority so it yields to the gate's high-priority
    // loads. On settle (load or error) start the next, so at most
    // BACKGROUND_CONCURRENCY are ever in flight. A background failure is
    // tolerable because loadSlideImages re-requests the URL with a fresh
    // Image() when that slide becomes current — there is no automatic refetch.
    img.setAttribute('fetchpriority', 'low');
    img.onload = loadNext;
    img.onerror = loadNext;
    img.src = url;
    preloaded.push(img);
  };
  for (let i = 0; i < Math.min(BACKGROUND_CONCURRENCY, urls.length); i += 1) {
    loadNext();
  }
};

// Awaitable loader for the CURRENT slide's images. Unlike preloadAssets
// (background, fire-and-forget), this resolves only once every image has
// loaded and REJECTS on the first failure — the slide must not display until
// its images are present, and a required image that can't load is an error.
export const loadSlideImages = (urls: string[]): Promise<void> => {
  if (!urls.length) {
    return Promise.resolve();
  }
  const win = window as Win;
  const preloaded: HtmlImage[] = win.preloaded ?? [];
  const loads = urls.map(
    (url) =>
      new Promise<void>((resolve, reject) => {
        const img = new Image() as HtmlImage;
        // Current-slide gate: high priority so these are scheduled ahead of the
        // low-priority background warming (in-flight requests aren't paused —
        // BACKGROUND_CONCURRENCY is what actually leaves headroom).
        img.setAttribute('fetchpriority', 'high');
        img.onload = () => resolve();
        img.onerror = () => reject(new Error(`Unable to load slide image: ${url}`));
        img.src = url;
        preloaded.push(img);
      }),
  );
  win.preloaded = preloaded;

  // No max-wait by design: every gated image is required, so the slide must not
  // display without it. A stalled image (TCP open, no bytes — neither onload nor
  // onerror) holds the gate, and the LMS overlay it keeps up, until it loads,
  // errors, or the learner reloads. A hard failure (onerror) still rejects here
  // immediately and routes to the error screen.
  return Promise.all(loads).then(() => undefined);
};

// Surface a failed slide load: clear the spinner, show the error screen, and
// notify the LMS. lms-ui clears its loading overlay only on SlideLoaded or
// Error, and a slide that can't display sends no SlideLoaded — so the Error
// notification is what releases the parent overlay. Callers re-throw after
// this so their success()/background-warm flow does not run on a failed load.
export const handleSlideLoadError = (dispatch: Dispatch, e: unknown) => {
  dispatch(update({ loading: false, slideError: true }));
  notifyError(e);
};

const getAndStartSlide = async ({
  dispatch,
  module,
  learnerId,
  updatedState,
  currentSlideId,
  previous,
  navItemId,
  loadSlideId,
}: {
  dispatch: Dispatch;
  module: Module;
  learnerId: string;
  updatedState: {};
  currentSlideId?: string;
  previous?: boolean;
  navItemId?: string;
  loadSlideId?: string;
}) => {
  notifySlideLoading();
  dispatch(
    update({
      loading: true,
      slideError: false,
    }),
  );
  dispatch(stopPlayer());
  dispatch(updateQuiz({ choicesShouldPulse: false, explanationButtonsShouldPulse: false }));
  showNextModule({ show: false });
  let slideResponse;
  if (previous) {
    if (!currentSlideId) {
      throw new Error('Current slide not provided');
    }

    slideResponse = await getPreviousSlideForLearner({
      learnerId,
      currentSlideId,
      navItemId,
    });
  } else if (loadSlideId) {
    slideResponse = await getSlideForLearner({
      learnerId,
      currentSlideId,
      loadSlideId,
    });
  } else {
    slideResponse = await getNextSlideForLearner({
      learnerId,
      currentSlideId,
      navItemId,
    });
  }
  let slideToLoad: Slide = slideResponse.data;
  if (slideToLoad.type !== CustomNavType) {
    slideToLoad = await loadSlideResources({
      slide: slideToLoad,
      learnerId,
    });
  }

  const { isLast, type, allComplete, learnerData, _id } = slideToLoad;
  if (isLast || type === CustomNavType) {
    showNextModule({
      show: learnerData?.completed || type === TemplateTypes.Explanation,
      unlocked: learnerData?.unlocked,
      completed:
        learnerData?.completed || (learnerData?.unlocked && type === TemplateTypes.Explanation),
    });
  }

  if (allComplete) {
    setCompleted();
  }

  const quizData = determineQuizData({
    slide: slideToLoad,
    clearSelected: true,
  });
  if (slideToLoad.type === TemplateTypes.CheckAllApply) {
    quizData.displayedEditId = '';
    if (quizData.completed) {
      const choices = slideToLoad.timeline.filter((r) => r.type === ResourceItemTypes.Answer);
      for (const choice of choices) {
        const isSelected = !!((quizData.selected?.indexOf(choice._id || '') ?? -1) > -1);
        const found = (isSelected && !choice.correct) || (!isSelected && choice.correct);
        if (found) {
          quizData.displayedEditId = choice._id;
          break;
        }
      }
    }
  }
  console.log('Dispatching quiz update ...');
  dispatch(updateQuiz(quizData));

  // Gate: do not reveal the slide until all of its images have downloaded.
  // notifySlideLoading() (top of this fn) already told the LMS to keep its
  // loading overlay up, and loading:true holds the in-app spinner — so this
  // wait is covered for both contexts. A required image that fails (or the
  // max-wait) rejects; the rejection propagates to the calling thunk, which
  // routes it through handleSlideLoadError — so the slide is never shown and
  // the success()/background-warm flow does not run on a failed load.
  await loadSlideImages(getUrlsForSlide(_id, module.assets));

  dispatch(
    update({
      ...updatedState,
      data: module,
      currentSlide: slideToLoad,
      loading: false,
    }),
  );

  notifySlideLoaded({ _id });
  return slideToLoad;
};

const updateHideExercisesAndLoadSlide = async (
  dispatch: Dispatch,
  learnerId: string,
  hide: boolean,
  settings: {},
) => {
  const hideResponse = await updateHideModuleExercisesForLearner(learnerId, hide);
  const module = hideResponse.data;
  await getAndStartSlide({
    dispatch,
    module,
    learnerId,
    updatedState: { settings },
  });
};

export const updateHideExercises =
  (hideExercises: boolean) => async (dispatch: Dispatch, getState: Function) => {
    try {
      const state = getState();
      const { slidesLitmos, learner } = state;
      const { settings } = slidesLitmos;
      const { learnerId } = learner;
      dispatch(load());
      await updateHideExercisesAndLoadSlide(dispatch, learnerId, hideExercises, settings);

      resetStatus();
      dispatch(success());
    } catch (err) {
      handleSlideLoadError(dispatch, err);
    }
  };

export const initialize = () => async (dispatch: Dispatch) => {
  try {
    console.log('Initializing module ...');
    dispatch(load());
    let learnerId = `_LUI-${uuidv4()}`;
    const win = window as Win;
    if (win.GetStudentID) {
      learnerId = win.GetStudentID();
    } else if (UseSession) {
      learnerId = 'session';
    }

    if (!learnerId) {
      throw new Error('Cannot get learner');
    }

    dispatch(updateLearner({ learnerId }));

    console.log('Getting module ...');
    const moduleResponse = await getModuleForLearner(learnerId);

    const module = moduleResponse.data;

    console.log('Getting course for module...');
    const courseResponse = await getCourseForLearner(module._courseId, learnerId);
    console.log('Dispatching update for course and setting updated slide state ...');
    const course = courseResponse.data;

    dispatch(updateCourse(course));
    const updatedState = {
      settings: {
        font: course.font,
        color: course.color,
        whiteLabel: course.whiteLabel,
      },
    };

    const currentSlide = await getAndStartSlide({
      dispatch,
      module,
      learnerId,
      updatedState,
    });

    // The current slide's own images were already loaded (awaited) by the
    // gate inside getAndStartSlide. Here we only background-load the REST of
    // the module — fire-and-forget — excluding the current slide's URLs so we
    // don't re-request them.
    const assets: ModuleAsset[] = module.assets ?? [];
    const backgroundUrls = assets
      .filter((a) => !a.slideIds.includes(currentSlide._id))
      .map((a) => a.url);
    // whiteLabel is a global (non-slide) asset — background only.
    if (course.whiteLabel) {
      backgroundUrls.push(course.whiteLabel);
    }
    preloadAssets(backgroundUrls);

    console.log('Dispatch success ...');
    dispatch(success());
  } catch (e: any) {
    if (e.response && e.response.status === 403) {
      console.error(e);
      dispatch(update({ loading: false, noAccess: true }));
    } else {
      handleSlideLoadError(dispatch, e);
    }
  }
};

export const initializeSlide = () => async (dispatch: Dispatch) => {
  try {
    console.log('Initializing slide ...');
    const slide = (window.parent as Win).slide as Slide;
    dispatch(updateLearner({ learnerId: 'SlidePreviewer' }));
    dispatch(
      update({
        currentSlide: slide,
      }),
    );

    const quizData = determineQuizData({ slide, clearSelected: true });
    dispatch(updateQuiz(quizData));
  } catch (e) {}
};

export const getNext =
  (ignoreCurrent?: boolean) => async (dispatch: Dispatch, getState: Function) => {
    try {
      console.log('Getting next slide ...');
      dispatch(load());
      const state = getState();
      const { slidesLitmos, learner } = state;
      const { currentSlide, data: module, settings, navItemId } = slidesLitmos;
      const { learnerId } = learner;

      /* Specific to running in LMS to update ToC state */
      const { learnerData, _id, type } = currentSlide;
      const { tries, started, correct, completed } = learnerData || {};
      postSlideData({
        tries,
        started,
        correct: !isExerciseSlide(type) || correct,
        completed,
        _id,
      });
      /* End chunk */

      await getAndStartSlide({
        dispatch,
        module,
        learnerId,
        updatedState: { settings },
        currentSlideId: !ignoreCurrent ? currentSlide?._id : undefined,
        navItemId,
      });

      dispatch(success());
    } catch (e) {
      handleSlideLoadError(dispatch, e);
    }
  };

export const getPrevious = () => async (dispatch: Dispatch, getState: Function) => {
  try {
    dispatch(load());
    const state = getState();
    const { slidesLitmos, learner } = state;
    const { data: module, settings, currentSlide, navItemId } = slidesLitmos;
    const { learnerId } = learner;
    await getAndStartSlide({
      dispatch,
      module,
      learnerId,
      updatedState: { settings },
      currentSlideId: currentSlide._id,
      previous: true,
      navItemId,
    });

    dispatch(success());
  } catch (e) {
    handleSlideLoadError(dispatch, e);
  }
};

export const getSlide = (slideId: string) => async (dispatch: Dispatch, getState: Function) => {
  try {
    console.log('Getting slide ...');
    dispatch(load());
    const state = getState();
    const { slidesLitmos, learner } = state;
    const { currentSlide, data: module, settings } = slidesLitmos;
    const { learnerId } = learner;

    await getAndStartSlide({
      dispatch,
      module,
      learnerId,
      updatedState: { settings },
      currentSlideId: currentSlide?._id,
      loadSlideId: slideId,
    });

    dispatch(success());
  } catch (e) {
    handleSlideLoadError(dispatch, e);
  }
};

export const markViewed = () => async (dispatch: Dispatch, getState: Function) => {
  try {
    if (!SlideIncluded) {
      const state = getState();
      const { slidesLitmos, learner, explanation } = state;
      const { currentSlide } = slidesLitmos;
      const { learnerId } = learner;
      const { autoAdvance } = explanation.player;
      const slideResponse = await markViewedForLearner({
        learnerId,
        currentSlideId: currentSlide._id,
      });

      const updatedSlide = cloneDeep(currentSlide);
      const newLearnerData = slideResponse.data?.learnerData;
      if (newLearnerData) {
        updatedSlide.learnerData = { ...updatedSlide.learnerData, ...newLearnerData };
      }

      const allComplete = slideResponse.data?.allComplete;
      const { isLast, learnerData, type } = updatedSlide;
      if (allComplete) {
        setCompleted();
      }

      if (isLast) {
        showNextModule({
          show: true,
          advanceNext: type === TemplateTypes.Explanation && autoAdvance,
          unlocked: learnerData?.unlocked,
          completed:
            learnerData?.completed || (learnerData?.unlocked && type === TemplateTypes.Explanation),
        });
      }

      /*
      // If this is the last slide and the learner has unlocked nav,
      // show the NEXT MODULE if
      // - slide is an Explainer OR
      // - slide is complete
      else if (
        isLast &&
        learnerData?.unlocked &&
        (type === TemplateTypes.Explanation || learnerData?.completed)
      ) {
        showNextModule({
          show: true,
          advanceNext: type === TemplateTypes.Explanation && autoAdvance,
        });
      }
*/
      const quizData = determineQuizData({ slide: updatedSlide });
      if (autoAdvance) {
        quizData.advance = true;
      }

      dispatch(update({ currentSlide: updatedSlide }));
      dispatch(updateQuiz(quizData));
    }
  } catch (e) {
    console.error(e);
  }
};

export const initialState: SlidePayload = {
  data: undefined,
  currentSlide: undefined,
  index: -1,
  settings: undefined,
  noAccess: false,
  slideError: false,
};

export const slidesLitmosReducer = (
  state: SlidePayload = initialState,
  action: SlideActionTypes,
) => {
  switch (action.type) {
    case actions.UPDATE:
      return {
        ...state,
        ...action.payload,
      };
    case actions.CLEAR: {
      console.log('Cleared slides to initial state');
      return initialState;
    }
    default:
      return state;
  }
};
